/**
 * What: Shows written answers to merchant-authored questions on the study results page.
 * Transport / data-flow: Renders the assembled diagnosticResponses slice; no fetching here.
 *
 */
import { ArmLabelDisplay, isImageUrl } from './ArmLabelDisplay'
import { productLanguage } from '@testfeed/product-language'
import {
  buildArmsRows,
  buildShopifyTreatmentIdentityMap,
  type ShopifySelectedStudyRun,
} from '@testfeed/shopify-contracts'
import { RespondentAvatar } from './RespondentAvatar'
import type { ReactNode } from 'react'
import type { ShopifyResultQuestions } from '@testfeed/shopify-contracts'
import { ServerQuestionNavigation } from './ServerQuestionNavigation'
import { ResultAnswerRegion } from './ResultNavigation'
import { AuthoredAnswersSkeleton } from './AuthoredAnswersSkeleton'

const copy = productLanguage.shopify.studyQuestions.results

/**
 * Renders merchant-authored questions and their written answers.
 *
 * Shows counts and text only. No shares, percentages, or rankings appear here: coded themes and
 * illustrative language are allowed from a diagnostic corpus, but prevalence claims would need
 * matched human calibration that does not exist for these runs.
 */
export function YourQuestionsTab({ selectedRun }: { selectedRun: ShopifySelectedStudyRun }) {
  const diagnostic = selectedRun.details?.diagnosticResponses
  const arms = selectedRun.details?.arms ?? []
  const identityByArmId = buildShopifyTreatmentIdentityMap(arms, selectedRun.treatmentMode)
  const imageByArmId = new Map(arms.map((arm) => [arm.armId, arm.imageUrl ?? null]))
  const descriptionByArmId = new Map(
    buildArmsRows(arms, selectedRun.treatmentMode).map((arm) => [arm.armId, arm.treatmentDescription ?? null]),
  )

  if (!diagnostic || diagnostic.questions.length === 0) {
    return (
      <s-section>
        <s-text color="subdued">{copy.emptyState}</s-text>
      </s-section>
    )
  }

  return (
    <s-stack direction="block" gap="large-100">
      <s-section>
        <s-stack direction="block" gap="small-200">
          <s-heading>{copy.heading}</s-heading>
          <s-text color="subdued">{copy.description}</s-text>
          <s-text color="subdued">{copy.syntheticOrigin}</s-text>
        </s-stack>
      </s-section>

      {diagnostic.questions.map((question) => (
        <s-section key={question.questionId}>
          <s-stack direction="block" gap="small-300">
            <s-stack direction="block" gap="small-100">
              <s-text type="strong">{question.label}</s-text>
              <AttachmentQuestionNote purpose={question.purpose} />
              <s-text color="subdued">
                {`${question.answers.length} ${copy.answersShown} ${question.responseCount} ${copy.total}`}
              </s-text>
            </s-stack>

            <s-stack direction="block" gap="small-200">
              {question.answers.map((answer, index) => (
                <AuthoredQuestionAnswer
                  key={`${question.questionId}-${answer.armId}-${index}`}
                  answer={{
                    id: `${question.questionId}-${answer.armId}-${index}`,
                    respondent: answer.respondentLabel,
                    armLabel: identityByArmId.get(answer.armId) ?? productLanguage.studyResults.tables.treatmentRole,
                    imageUrl: imageByArmId.get(answer.armId) ?? (isImageUrl(answer.armLabel) ? answer.armLabel : null),
                    description: descriptionByArmId.get(answer.armId) ?? null,
                    text:
                      answer.response ||
                      (answer.assessment === 'unavailable' ? 'No attachment check was available.' : ''),
                    assessment: diagnosticAssessment(answer),
                  }}
                />
              ))}
            </s-stack>
          </s-stack>
        </s-section>
      ))}
    </s-stack>
  )
}

function AttachmentQuestionNote({
  purpose,
}: {
  purpose?: 'standard' | 'merchant_authored' | 'attachment_verification'
}) {
  if (purpose !== 'attachment_verification') return null
  return (
    <s-text color="subdued">
      Attachment checks are separate from the test score. They show what was reported about the supplied image; they do
      not confirm that every detail was understood.
    </s-text>
  )
}

/** Original question sections and respondent rows, with selection/counts/text supplied by the server. */
export function YourQuestionsView({ data, intro }: { data: ShopifyResultQuestions; intro: ReactNode }) {
  return (
    <s-stack direction="block" gap="large-100">
      {intro}
      <div id="result-answer-controls" style={{ scrollMarginTop: 16 }}>
        <ServerQuestionNavigation data={data.navigation} />
      </div>
      <ResultAnswerRegion loading={<AuthoredAnswersSkeleton groups={data.groups} />}>
        {data.groups.map((group) => (
          <s-section key={group.id}>
            <s-stack direction="block" gap="small-300">
              <s-stack direction="block" gap="small-100">
                <s-text type="strong">{group.title}</s-text>
                <QuestionDetail text={group.notice} />
                <s-text color="subdued">{group.count}</s-text>
              </s-stack>
              <s-stack direction="block" gap="small-200">
                {group.answers.map((answer) => (
                  <AuthoredQuestionAnswer key={answer.id} answer={answer} />
                ))}
              </s-stack>
            </s-stack>
          </s-section>
        ))}
      </ResultAnswerRegion>
      <ServerQuestionNavigation data={data.navigation} pagingOnly />
    </s-stack>
  )
}

/** Same avatar, metadata row and answer alignment for all authored-answer renderers. */
export function AuthoredQuestionAnswer({
  answer,
}: {
  answer: ShopifyResultQuestions['groups'][number]['answers'][number]
}) {
  return (
    <s-box background="subdued" borderRadius="base" padding="base" data-testid="merchant-question-answer">
      <s-grid gridTemplateColumns="auto minmax(0, 1fr)" gap="small-300" alignItems="start">
        <RespondentAvatar respondentLabel={answer.respondent} size="lg" />
        <s-stack direction="block" gap="small-100">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-text type="strong">{answer.respondent}</s-text>
            {answer.imageUrl && <ArmLabelDisplay label={answer.imageUrl} size="small" />}
            <s-badge tone="info">{answer.armLabel}</s-badge>
            <QuestionDetail text={answer.description} />
          </s-stack>
          <s-text>{answer.text}</s-text>
          <QuestionDetail text={answer.assessment} />
        </s-stack>
      </s-grid>
    </s-box>
  )
}
function QuestionDetail({ text }: { text: string | null }) {
  if (!text) return null
  return <s-text color="subdued">{text}</s-text>
}

// Retired raw-data adapter only; active results receive the exact ready assessment from the server.
function diagnosticAssessment(answer: { assessment?: 'inconclusive' | 'unavailable'; assessmentReason?: string }) {
  if (!answer.assessment) return null
  const label = answer.assessment === 'inconclusive' ? 'Inconclusive attachment check' : 'Attachment check unavailable'
  return answer.assessmentReason ? `${label}: ${answer.assessmentReason}` : label
}
